import classnames from "classnames";
import Button from "components/Button/Button";
import Collapsible from "components/Collapsible/Collapsible";
import { FFMultiSelect, PageItemActions } from "funkis-foundation";
import Image from "funkis-template/components/core/Image";
import Text from "funkis-template/components/core/Text";
import SelectItem from "funkis-template/components/core/version-2/SelectItem";
import PageItemScrollBase from "funkis-template/components/scroll/PageItemScrollBase";
import { useIOMultiSelect } from "funkis-template/hooks/io/multiselect";
import { useIsShowingTopBar } from "funkis-template/hooks/player";
import { MultipleChoiceInputScrollBlock } from "funkis-template/models/pageitem";
import { PageItem } from "funkis-template/models/player";
import { getText } from "funkis-template/utils/textUtils";
import React, { FC, useEffect, useState } from "react";

const PageItemIOMultipleChoiceInput: FC<{
	correctDelay?: number;
	pageItem: PageItem<MultipleChoiceInputScrollBlock> & {
		// we're keeping the status in "completionStatus" instead of "status" to bypass strange
		// re-reveal behavior that was triggered by switching pageItem.status directly
		completionStatus: PageItem<MultipleChoiceInputScrollBlock>["status"];
	};
}> = (props) => {
	const { pageItem, correctDelay = 0 } = props;
	const { cmtPageItem } = pageItem;
	const shouldBlurImageBeforeAnswer =
		cmtPageItem.blur_image_before_answer === "yes";

	const isShowingTopBar = useIsShowingTopBar(pageItem.type);

	const instruction = cmtPageItem.instruction;
	const imageSize = cmtPageItem.image_size;
	const imageAlign = cmtPageItem.image_align;
	const maxHeight = cmtPageItem.image_max_height;
	const imageMarginsMobile = cmtPageItem.image_margins_mobile !== "no";
	const optionAppearance = cmtPageItem.option_appearance;

	const isCompleted = pageItem.completionStatus === "completed";
	const shouldCorrect = cmtPageItem.correct === "yes";
	const [showNextButton, setShowNextButton] = useState(
		cmtPageItem.show_next_button === "always"
	);

	const [blurDuration] = useState(
		!isCompleted && shouldBlurImageBeforeAnswer ? 1.5 : 0
	);

	const {
		options,
		isCorrect,
		minSelections,
		maxSelections,
		isFinished: isSelectionFinished,
		hasConfirmationStep,
		hasConfirmedSelection,
		setHasConfirmedSelection,
		ffMultiSelectProps,
	} = useIOMultiSelect({ cmtPageItem });

	const getMode = (minSelections: number, maxSelections: number) => {
		if (minSelections === 1 && maxSelections === 1) return "radio";
		else return "select";
	};
	const mode = getMode(minSelections, maxSelections);
	const { selectedIndexes } = ffMultiSelectProps;

	const revealDoneButton =
		hasConfirmationStep && isSelectionFinished && !isCompleted;

	useEffect(() => {
		let timeoutId: number | undefined;
		if (isCompleted) {
			const delay = shouldCorrect ? correctDelay * options.length : 0.6;
			if (cmtPageItem.show_next_button === "on done") {
				timeoutId = window.setTimeout(() => {
					setShowNextButton(true);
				}, delay * 1000);
			}
		} else {
			cmtPageItem.show_next_button === "on done" && setShowNextButton(false);
		}

		return () => {
			timeoutId && window.clearTimeout(timeoutId);
		};
	}, [
		isCompleted,
		cmtPageItem.show_next_button,
		shouldCorrect,
		correctDelay,
		options.length,
	]);

	useEffect(() => {
		if (cmtPageItem.show_next_button === "on select") {
			setShowNextButton(selectedIndexes.length ? true : false);
		}
	}, [selectedIndexes, cmtPageItem.show_next_button]);

	useEffect(
		function handleCompletionStatus() {
			const isCompleted = !hasConfirmationStep
				? isSelectionFinished
				: isSelectionFinished && hasConfirmedSelection;
			const prevStatus = pageItem.status;
			const nextStatus = isCompleted ? "completed" : "incomplete";
			nextStatus !== prevStatus &&
				PageItemActions.updatePageItem(pageItem.id, {
					completionStatus: isCompleted ? "completed" : "incomplete",
				});
		},
		[
			isCompleted,
			isSelectionFinished,
			hasConfirmedSelection,
			hasConfirmationStep,
			pageItem.id,
			pageItem.status,
		]
	);

	const getOptionColumnClassnames = () => {
		const columnClassnamesByName = {
			one: "col-md-12",
			two: "col-md-6",
			three: "col-md-4",
		};
		const name = cmtPageItem.number_of_option_columns;
		return columnClassnamesByName[name] || columnClassnamesByName["one"];
	};

	return (
		<PageItemScrollBase
			{...props}
			showNextButton={showNextButton}
			className="px-0 mx-0"
			containerClassName="w-100"
			renderFunction={({ viewVisible }) => (
				<React.Fragment>
					{/* IMAGE */}
					{cmtPageItem.image && (
						<div
							className={classnames(
								imageMarginsMobile ? "container-fluid" : ""
							)}
						>
							<div
								className={classnames(
									"row m-0 mb-5 animated",
									viewVisible ? "fadeIn" : "fadeOut"
								)}
							>
								<Image
									src={cmtPageItem.image}
									className={classnames(
										imageMarginsMobile ? "col-12" : "w-100 px-sm-0 px-md-5"
									)}
									size={imageSize}
									maxHeight={maxHeight}
									align={imageAlign}
									style={{
										filter: shouldBlurImageBeforeAnswer
											? !isCompleted && "blur(20px)"
											: "blur(0)",
										transition:
											shouldBlurImageBeforeAnswer && `filter ${blurDuration}s`,
									}}
									children={undefined}
									autoSize={undefined}
									background={undefined}
									isZoomable={undefined}
								/>
							</div>
						</div>
					)}

					<div className="container-fluid">
						{/* TEXT BLOCK */}
						<div
							className={classnames(
								"row m-0 mb-5 animated",
								viewVisible ? "fadeIn" : "fadeOut"
							)}
						>
							{/* QUESTION */}
							<Text
								tagName="h2"
								textId={cmtPageItem.question}
								className="col-sm-12 mb-4 font-weight-bold"
							/>

							{/* INSTRUCTION */}
							<Text
								textId={instruction}
								className="col-sm-9 instruction font-italic"
							/>
						</div>
						{/* OPTIONS */}

						<Text
							textId={cmtPageItem.validation_tooltip}
							tagName="div"
							hideIfMissing={true}
							style={{
								position: "sticky",
								top: isShowingTopBar ? 50 : 0,
								backgroundColor: "black",
								color: "white",
								padding: "1rem",
								marginBottom: "1rem",
								zIndex: 5,
							}}
							variables={{
								selections: ffMultiSelectProps.selectedIndexes.length,
								minSelections: ffMultiSelectProps.minSelections,
								maxSelections: ffMultiSelectProps.maxSelections,
							}}
						/>

						<FFMultiSelect
							{...ffMultiSelectProps}
							className={classnames("row m-0")}
							buttonClassName={classnames("mb-4", getOptionColumnClassnames())}
						>
							{options.map((option, index) => {
								const status =
									(isCompleted &&
										shouldCorrect &&
										option.isSelected &&
										option.isCorrect &&
										"selected-correct") ||
									(isCompleted &&
										shouldCorrect &&
										option.isSelected &&
										!option.isCorrect &&
										"selected-incorrect") ||
									(isCompleted &&
										shouldCorrect &&
										!option.isSelected &&
										option.isCorrect &&
										"unselected-correct") ||
									(isCompleted &&
										shouldCorrect &&
										!option.isSelected &&
										!option.isCorrect &&
										"unselected-incorrect") ||
									undefined;

								let itemTextProps;
								switch (optionAppearance) {
									case "Only heading":
										itemTextProps = { textId: option.option };
										break;
									case "Only body":
										itemTextProps = { bodyTextId: option.option_description };
										break;
									case "Both heading + body":
									default:
										itemTextProps = {
											textId: option.option,
											bodyTextId: option.option_description,
										};
										break;
								}

								return (
									<SelectItem
										key={`multi-choice-${index}`}
										mode={mode}
										index={index}
										correct={cmtPageItem.correct !== "no"}
										correctDelay={correctDelay}
										image={option.image}
										selected={option.isSelected}
										status={status}
										completed={isCompleted}
										className={classnames(
											"animated",
											viewVisible ? "fadeInUp" : "fadeOut"
										)}
										style={{ animationDelay: index * 0.15 + "s" }}
										text={undefined}
										{...itemTextProps}
									/>
								);
							})}
						</FFMultiSelect>

						{/* ANSWER - Shows if COMPLETED and if there is feedback text */}
						<Collapsible
							open={Boolean(
								isCompleted &&
									(getText(cmtPageItem.feedback_correct) ||
										getText(cmtPageItem.feedback_incorrect))
							)}
							variant="collapse"
						>
							<div className="row m-0 mt-2">
								<Text
									className={classnames(
										"col-9 p-md",
										isCompleted ? "animated fadeInUp" : "opacity-0"
									)}
									textId={
										isCorrect
											? cmtPageItem.feedback_correct
											: cmtPageItem.feedback_incorrect
									}
								/>
							</div>
						</Collapsible>

						{/* DONE BUTTON */}
						<Collapsible open={revealDoneButton} variant="fade-out-collapse">
							<div
								className={classnames("d-flex flex-column align-items-center")}
							>
								<Button
									disabled={!revealDoneButton}
									onClick={() => {
										setHasConfirmedSelection(true);
									}}
								>
									<Text textId="D24115A3-B9A4-4B40-90F1-5E1213241A84" />
								</Button>
							</div>
						</Collapsible>
					</div>
				</React.Fragment>
			)}
		/>
	);
};

export default PageItemIOMultipleChoiceInput;
